import { useState, useEffect } from 'react';
import { Box, Container, Stack, Typography, useTheme } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import useTable from 'hooks/useTable';
import MuiDataGrid from 'components/MuiDataGrid';
import { FormProps, IMedication } from '../PatientList/api/type';
import ListActionButtons from './ListActionButtons';
import MedicationDialog from './MedicationDialog';
import { formatStringToDate } from 'utils/formatting';
import { updateMedicationHidden } from 'apis/admin';
import useNotification from 'hooks/useNotification';

interface Props extends FormProps {
  defaultList?: IMedication[];
}

const MedicationList = (props: Props) => {
  const { setValue, defaultList } = props;
  const { palette } = useTheme();
  const { onSuccess, onFail } = useNotification();
  const { list, setList } = useTable<IMedication>();
  const [defaultValue, setDefaultValue] = useState<Partial<IMedication> | null>(
    null
  );

  useEffect(() => setList(defaultList ?? []), [defaultList, setList]);
  useEffect(() => {
    setValue(
      'medication',
      // api 업데이트할 형태로 오브젝트를 저장함
      list.map(item => ({
        ...item,
        prescriptionTime: formatStringToDate(item.prescriptionTime),
        medicationTime: formatStringToDate(item.medicationTime),
      }))
    );
  }, [list, setValue]);

  const onCreateList = (data: IMedication) => setList(list.concat(data));
  const onUpdateList = (data: IMedication) =>
    setList(
      list.map(item =>
        item.ptMedicationNo === data.ptMedicationNo ? data : item
      )
    );

  const ActionButtons = ({ row }: { row: IMedication }) => {
    const onUpdate = () => setDefaultValue(row);

    const onToggleHidden = async () => {
      const nextHidden = !row.isHidden; // 클릭 후 상태(목표)

      try {
        await updateMedicationHidden(row.ptMedicationUuid);

        setList(prev =>
          prev.map(item =>
            item.ptMedicationNo === row.ptMedicationNo
              ? { ...item, isHidden: nextHidden }
              : item
          )
        );

        onSuccess(
          nextHidden ? '숨김 처리되었습니다.' : '숨김이 해제되었습니다.'
        );
      } catch (error) {
        console.error('숨김 처리 중 오류 발생:', error);
        onFail(
          nextHidden
            ? '숨김 처리에 실패하였습니다.'
            : '숨김 해제에 실패하였습니다.',
          error
        );
      }
    };

    const onDelete = () =>
      setList(prev =>
        prev.filter(item => item.ptMedicationNo !== row.ptMedicationNo)
      );

    return (
      <ListActionButtons
        onUpdate={onUpdate}
        onToggleHidden={onToggleHidden}
        isHidden={row.isHidden}
        onDelete={onDelete}
      />
    );
  };

  const columns: GridColDef[] = [
    { field: 'ptMedicationNo', headerName: '투약번호' },
    {
      field: 'prescriptionTime',
      headerName: '처방시간',
      valueFormatter: ({ value }) => formatStringToDate(value, 'hh:mm a'),
    },
    {
      field: 'medicationTime',
      headerName: '투여시간',
      valueFormatter: ({ value }) => formatStringToDate(value, 'hh:mm a'),
    },
    { field: 'medicationId', headerName: '성분명코드' },
    { field: 'medicationName', headerName: '일반명' },
    {
      align: 'right',
      field: 'actions',
      headerName: '',
      renderCell: ({ row }) => <ActionButtons row={row} />,
    },
  ];

  return (
    <Container
      maxWidth={false}
      disableGutters
      component="section"
      sx={{ background: '#fff', p: 2 }}
    >
      <Stack spacing={2}>
        <Box display="flex" alignItems="center">
          <Typography variant="h5" sx={{ color: `${palette.primary.main}` }}>
            투약기록지 리스트 (수정가능)
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              color: '#555555a4',
              marginLeft: '30px',
            }}
          >
            (추가 시 스마트널스 ENR로 문의해주세요.)
          </Typography>
        </Box>

        <MuiDataGrid
          columns={columns}
          rows={list.map(item => ({ ...item, id: item.ptMedicationNo }))}
        />
      </Stack>

      {defaultValue && (
        <MedicationDialog
          isUpdate={defaultValue.ptMedicationNo ? true : false}
          defaultValue={defaultValue}
          onClose={() => setDefaultValue(null)}
          onSearchNo={(no: number) =>
            list.find(item => item.ptMedicationNo === no) ? true : false
          }
          onSave={defaultValue.ptMedicationNo ? onUpdateList : onCreateList}
        />
      )}
    </Container>
  );
};

export default MedicationList;
